import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSaveDb, ensureSaveDb } from '@/lib/useSaveDb'
import { withTimeout } from '@/lib/saveTimeout'
import { ValidationError } from '@/lib/errorReporter'
import { alertDialog } from '@/components/Dialogs'
import Modal from '@/components/Modal'
import { Scale, Boxes, Package, AlertTriangle } from 'lucide-react'

// Admin-only "Balance packout" (mig 902). A run's packout % =
// packed_weight_kg / tipped_net_weight_kg * 100. When the recorded weights are
// systematically off (a mis-set bin tare, a scale drift) the admin can nudge a
// fixed kg per item to balance it:
//   * BIN lever    → net += delta * bin_quantity on every tipping_log row
//                    (denominator). No billing/label impact.
//   * PALLET lever → net (+ gross) += delta on every eligible LEAF pallet
//                    (numerator). Feeds invoices / SSCC labels / dispatch notes,
//                    so it skips dispatched/consigned/mission + multi-line pallets
//                    and requires an explicit confirm.
// The server is authoritative on which items qualify; the preview here is a
// directional estimate, and the success summary shows the real applied counts.

interface RunLite {
  run_id:               string
  run_number:           string
  tipped_net_weight_kg: number
  packed_weight_kg:     number
  packout_percent:      number | null
}

interface BinLite {
  bin_quantity:  number | null
  net_weight_kg: number | null
}

interface PalletLite {
  status?:               string | null
  net_weight_kg?:        number | null
  ppecb_consignment_id?: string | null
  truck_consignment_id?: string | null
  loading_mission_id?:   string | null
  line_count?:           number
  is_buildup?:           boolean
}

interface Props {
  run:     RunLite
  bins:    BinLite[]
  pallets: PalletLite[]
  onClose: () => void
}

type Lever = 'BIN' | 'PALLET'

const fmtKg  = (n: number) => `${Math.round(n).toLocaleString('en-ZA')} kg`
const fmtPct = (n: number | null) => (n == null ? '—' : `${n.toFixed(1)}%`)

export default function BalancePackoutDialog({ run, bins, pallets, onClose }: Props) {
  const qc = useQueryClient()
  // Lock-free isolated client + fail-fast timeout: the shared `supabase`
  // client's navigator.locks auth reads stall a write for minutes on a busy
  // page (Runs runs many concurrent useQuery hooks), which presented here as an
  // infinite "Applying…" spinner. See useSaveDb.ts / saveTimeout.ts.
  const saveDb = useSaveDb()
  const [lever,     setLever]     = useState<Lever>('BIN')
  const [deltaStr,  setDeltaStr]  = useState('')
  const [reason,    setReason]    = useState('')
  const [confirmed, setConfirmed] = useState(false)

  const delta = Number(deltaStr)
  const deltaValid =
    deltaStr.trim() !== '' && Number.isFinite(delta) && delta !== 0 && Math.abs(delta) <= 1000

  // Bin side — every PHYSICAL bin moves by delta (× bin_quantity).
  const totalBins = useMemo(
    () => bins.reduce((s, b) => s + (Number(b.bin_quantity) || 0), 0),
    [bins],
  )

  // Pallet side — approximate the server's eligible set for the preview.
  const eligiblePallets = useMemo(
    () => pallets.filter(p =>
      (p.net_weight_kg ?? 0) > 0 &&
      (p.status ?? '') !== 'DISPATCHED' &&
      !p.ppecb_consignment_id && !p.truck_consignment_id && !p.loading_mission_id &&
      (p.line_count ?? 1) <= 1 && !p.is_buildup),
    [pallets],
  )
  const weighedCount = useMemo(
    () => pallets.filter(p => (p.net_weight_kg ?? 0) > 0).length,
    [pallets],
  )
  const skippedCount = Math.max(0, weighedCount - eligiblePallets.length)

  // Floor check — mirror the server's all-or-nothing refusal for nicer UX.
  const floorBreached = useMemo(() => {
    if (!deltaValid || delta >= 0) return false
    if (lever === 'BIN') {
      return bins.some(b => (Number(b.net_weight_kg) || 0) + delta * (Number(b.bin_quantity) || 0) < 0)
    }
    return eligiblePallets.some(p => (Number(p.net_weight_kg) || 0) + delta < 0)
  }, [deltaValid, delta, lever, bins, eligiblePallets])

  const tipped = run.tipped_net_weight_kg
  const packed = run.packed_weight_kg
  const d = deltaValid ? delta : 0
  const newTipped = lever === 'BIN'    ? tipped + d * totalBins                : tipped
  const newPacked = lever === 'PALLET' ? packed + d * eligiblePallets.length   : packed
  const newPackout = newTipped > 0 ? (newPacked / newTipped) * 100 : null

  const itemCount = lever === 'BIN' ? bins.length : eligiblePallets.length

  const canSubmit =
    deltaValid &&
    !floorBreached &&
    reason.trim().length > 0 &&
    itemCount > 0 &&
    (lever === 'BIN' || confirmed)

  const apply = useMutation({
    mutationFn: async () => {
      const db = saveDb ?? await ensureSaveDb()
      if (!db) throw new ValidationError('Session not ready — refresh and try again.')
      const fn = lever === 'BIN' ? 'admin_adjust_run_bin_weights' : 'admin_adjust_run_pallet_weights'
      const { data, error } = await withTimeout(
        db.rpc(fn, {
          p_run_id:   run.run_id,
          p_delta_kg: delta,
          p_reason:   reason.trim(),
        }),
        `Balance packout: ${fn}`,
      )
      if (error) throw error
      return data as Record<string, number | null>
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['runs'] })
      qc.invalidateQueries({ queryKey: ['run-pallets'] })
      qc.invalidateQueries({ queryKey: ['run-tipping'] })
      qc.invalidateQueries({ queryKey: ['pallets'] })

      const msg = lever === 'BIN'
        ? `Adjusted ${data.bins_adjusted ?? 0} bin(s) across ${data.rows_adjusted ?? 0} tip entr${(data.rows_adjusted ?? 0) === 1 ? 'y' : 'ies'}. `
          + `Tipped is now ${fmtKg(Number(data.tipped_net_weight_kg ?? 0))}, packout ${fmtPct(data.packout_percent ?? null)}.`
        : `Adjusted ${data.pallets_adjusted ?? 0} pallet(s)`
          + ((data.pallets_skipped ?? 0) > 0 ? `, skipped ${data.pallets_skipped} (dispatched / consigned / multi-line)` : '')
          + `. Packed is now ${fmtKg(Number(data.packed_weight_kg ?? 0))}, packout ${fmtPct(data.packout_percent ?? null)}.`

      void alertDialog({ title: 'Packout balanced', message: msg, tone: 'info' })
      onClose()
    },
    onError: (e: unknown) => {
      void alertDialog({
        title:   'Could not balance',
        message: (e as { message?: string })?.message ?? 'Unknown error',
        tone:    'danger',
      })
    },
  })

  // Dirty baseline (K-forms #5): the fields that hold typed work, synchronous
  // at mount, so a backdrop tap can't silently discard the reason / confirm.
  // `lever` is deliberately excluded — switching it only changes which counts
  // the panel displays, so merely looking at the other lever must not arm a
  // "Discard changes?" over nothing.
  const snapshot = `${deltaStr}|${reason}|${confirmed}`

  return (
    <Modal
      onClose={onClose}
      snapshot={snapshot}
      onSubmit={() => { if (canSubmit && !apply.isPending) apply.mutate() }}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <Scale size={16} className="text-emerald-400 shrink-0" />
          <span className="truncate">
            Balance packout
            <span className="text-xs font-normal text-fg-muted ml-2">{run.run_number}</span>
          </span>
        </span>
      }
      footer={attemptClose => (
        <footer className="px-4 py-3 border-t hairline flex items-center justify-end gap-2 shrink-0">
          <button type="button" onClick={attemptClose} className="text-xs px-3 py-2 rounded-lg text-fg-muted hover:bg-surface">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || apply.isPending}
            className="text-xs font-semibold px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
            <Scale size={14} />
            {apply.isPending ? 'Applying…' : 'Apply adjustment'}
          </button>
        </footer>
      )}>
      <div className="space-y-4">
          {/* Lever toggle */}
          <div>
            <label className="text-xs font-semibold text-fg-muted uppercase tracking-wider">Adjust</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {([
                { key: 'BIN' as Lever,    icon: Boxes,   title: 'Bins (tipped)',  sub: `${bins.length} entr${bins.length === 1 ? 'y' : 'ies'} · ${totalBins} bins` },
                { key: 'PALLET' as Lever, icon: Package, title: 'Pallets (packed)', sub: `${eligiblePallets.length} eligible${skippedCount > 0 ? ` · ${skippedCount} locked` : ''}` },
              ]).map(opt => {
                const Icon = opt.icon
                const active = lever === opt.key
                return (
                  <button key={opt.key} type="button" onClick={() => setLever(opt.key)}
                    className={[
                      'rounded-xl border px-3 py-2.5 text-left transition-colors',
                      active ? 'border-emerald-500 bg-emerald-900/20' : 'hairline hover:border-line',
                    ].join(' ')}>
                    <div className="flex items-center gap-1.5">
                      <Icon size={14} className={active ? 'text-emerald-400' : 'text-fg-muted'} />
                      <span className="text-sm font-semibold">{opt.title}</span>
                    </div>
                    <p className="text-[11px] text-fg-muted mt-0.5">{opt.sub}</p>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Delta input */}
          <label className="block">
            <span className="block text-xs font-semibold text-fg-muted uppercase tracking-wider">
              Kg per {lever === 'BIN' ? 'bin' : 'pallet'} <span className="text-red-400">*</span>
            </span>
            <input
              type="number" inputMode="decimal" step="0.1"
              value={deltaStr}
              onChange={e => setDeltaStr(e.target.value)}
              placeholder="e.g. 2 to add, -1.5 to subtract"
              className="mt-1 w-full surface border hairline-strong rounded-lg px-3 py-2 text-sm text-fg placeholder-gray-500 focus:outline-none focus:border-emerald-500"
            />
            <p className="text-[11px] text-fg-muted mt-1">
              Applied uniformly to every {lever === 'BIN' ? 'physical bin' : 'eligible pallet'}. Negative subtracts. Max ±1000.
            </p>
            {floorBreached && (
              <p className="text-[11px] text-red-400 mt-1">
                That decrease would push a {lever === 'BIN' ? 'tipped' : 'pallet'} weight below 0 kg — use a smaller value.
              </p>
            )}
          </label>

          {/* Live preview */}
          <div className="rounded-xl border hairline bg-gray-950/40 px-3 py-2.5 space-y-1.5">
            <p className="text-[11px] text-fg-muted uppercase tracking-wider">Projected (estimate)</p>
            <PreviewRow label="Tipped" from={fmtKg(tipped)} to={fmtKg(newTipped)} changed={lever === 'BIN' && deltaValid} />
            <PreviewRow label="Packed" from={fmtKg(packed)} to={fmtKg(newPacked)} changed={lever === 'PALLET' && deltaValid} />
            <PreviewRow label="Packout" from={fmtPct(run.packout_percent)} to={fmtPct(newPackout)} changed={deltaValid} emphasise />
          </div>

          {/* Pallet-mode hard warning + required confirm */}
          {lever === 'PALLET' && (
            <div className="text-xs text-amber-200 bg-amber-900/20 border border-amber-800/50 rounded-lg px-3 py-2.5 space-y-2">
              <p className="flex items-start gap-1.5 leading-snug">
                <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-400" />
                <span>
                  Pallet weights feed <b>grower / customer invoices, SSCC labels and dispatch notes</b>.
                  Dispatched / consigned / mission and multi-line build-up pallets are <b>skipped</b>
                  {skippedCount > 0 ? ` (${skippedCount} here)` : ''}.
                </span>
              </p>
              <label className="flex items-center gap-2 cursor-pointer pl-5">
                <input type="checkbox" checked={confirmed}
                  onChange={e => setConfirmed(e.target.checked)}
                  className="h-4 w-4 accent-amber-500" />
                <span>I understand — adjust the {eligiblePallets.length} eligible pallet(s)</span>
              </label>
            </div>
          )}

          {/* Reason */}
          <label className="block">
            <span className="block text-xs font-semibold text-fg-muted uppercase tracking-wider">
              Reason <span className="text-red-400">*</span>
            </span>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. bin tare was set 2 kg low for this run"
              className="mt-1 w-full surface border hairline-strong rounded-lg px-3 py-2 text-sm text-fg placeholder-gray-500 focus:outline-none focus:border-emerald-500"
            />
            <p className="text-[11px] text-fg-muted mt-1">Kept on the audit trail.</p>
          </label>

          {itemCount === 0 && (
            <p className="text-[11px] text-amber-300 flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              {lever === 'BIN' ? 'This run has no tipped bins to adjust.' : 'No eligible pallets on this run to adjust.'}
            </p>
          )}
      </div>
    </Modal>
  )
}

function PreviewRow({ label, from, to, changed, emphasise }: {
  label: string; from: string; to: string; changed: boolean; emphasise?: boolean
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-fg-muted">{label}</span>
      <span className="flex items-center gap-1.5">
        <span className={changed ? 'text-fg-muted line-through' : 'text-fg'}>{from}</span>
        {changed && <span className="text-fg-muted">→</span>}
        {changed && <span className={emphasise ? 'text-emerald-400 font-semibold' : 'text-fg font-medium'}>{to}</span>}
      </span>
    </div>
  )
}
